import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useAuth } from "../../context/AuthContext";

export const AdminNewsWrite = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [sportId, setSportId] = useState("");
  const [sports, setSports] = useState([]);
  const [content, setContent] = useState("");
  const [references, setReferences] = useState([""]);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Fetch sports on component mount
  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/rss/api/sports`
      );

      if (response.ok) {
        const sportsData = await response.json();
        // Extract the sports array from the response object
        setSports(sportsData.sports || []);
      }
    } catch (error) {
      console.error("Error fetching sports:", error);
      setSports([]); // Set empty array on error
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const compressedFiles = [];
    const MAX_SIZE_BYTES = 400 * 1024; // 400KB (target for database storage)
    const MAX_ALLOWED_SIZE = 10 * 1024 * 1024; // 10MB limit

    if (files.length === 0) return;

    setIsCompressing(true);

    // Check for files exceeding 10MB limit
    const oversizedFiles = files.filter((file) => file.size > MAX_ALLOWED_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => file.name).join(", ");
      alert(
        `The following files exceed the maximum allowed size of 10MB: ${fileNames}. Please select smaller files.`
      );
      // Reset the file input
      e.target.value = "";
      setIsCompressing(false);
      return;
    }

    for (const file of files) {
      try {
        if (file.size <= MAX_SIZE_BYTES) {
          // Image is already under 400KB, use as is (no compression needed)
          compressedFiles.push(file);
        } else {
          // Compress image with multi-stage approach based on original size

          let compressedFile;
          const originalSizeKB = file.size / 1024;

          if (originalSizeKB <= 1000) {
            // For images 400KB - 1MB: Light compression to preserve quality
            compressedFile = await imageCompression(file, {
              maxSizeMB: 0.38, // 380KB target
              maxWidthOrHeight: 1920, // Keep high resolution
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.85, // High quality
            });
          } else if (originalSizeKB <= 3000) {
            // For images 1MB - 3MB: Moderate compression
            compressedFile = await imageCompression(file, {
              maxSizeMB: 0.35, // 350KB target
              maxWidthOrHeight: 1600, // Slightly reduced resolution
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.75, // Good quality
            });
          } else {
            // For images 3MB+: More aggressive initial compression
            compressedFile = await imageCompression(file, {
              maxSizeMB: 0.32, // 320KB target
              maxWidthOrHeight: 1400, // Reduced resolution
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.65, // Moderate quality
            });
          }

          // Stage 2: If still too large, compress more
          if (compressedFile.size > MAX_SIZE_BYTES) {
            compressedFile = await imageCompression(compressedFile, {
              maxSizeMB: 0.35, // 350KB target
              maxWidthOrHeight: 1200, // Further reduced resolution
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.6, // Lower quality
            });
          }

          // Stage 3: Final compression if still needed
          if (compressedFile.size > MAX_SIZE_BYTES) {
            compressedFile = await imageCompression(compressedFile, {
              maxSizeMB: 0.38, // 380KB target
              maxWidthOrHeight: 1000, // Smaller resolution
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.5, // Lower quality
            });
          }

          if (compressedFile.size > MAX_SIZE_BYTES) {
            console.warn(
              `Warning: Image ${
                file.name
              } could not be compressed below 400KB. Final size: ${(
                compressedFile.size / 1024
              ).toFixed(1)}KB`
            );
            alert(
              `Warning: Image "${
                file.name
              }" could not be compressed below 400KB. Final size: ${(
                compressedFile.size / 1024
              ).toFixed(1)}KB. It may not upload properly.`
            );
          }

          compressedFiles.push(compressedFile);
        }
      } catch (error) {
        console.error("Image compression failed:", error);
        alert(
          `Failed to compress image "${file.name}". Please try a different image.`
        );
        // Skip this file instead of using the original
        continue;
      }
    }

    // Add compressed images to existing ones
    setImages([...images, ...compressedFiles]);

    setIsCompressing(false);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setVideo(null);
      return;
    }

    const MAX_VIDEO_SIZE = 70 * 1024 * 1024; // 70MB limit

    if (file.size > MAX_VIDEO_SIZE) {
      alert(
        `Video file "${
          file.name
        }" is too large! Maximum allowed size is 70MB. Current size: ${(
          file.size /
          1024 /
          1024
        ).toFixed(1)}MB. Please select a smaller video file.`
      );
      // Reset the file input
      e.target.value = "";
      setVideo(null);
      return;
    }

    setVideo(file);
  };

  const handleReferenceChange = (index, value) => {
    const newReferences = [...references];
    newReferences[index] = value;
    setReferences(newReferences);
  };

  const addReferenceField = () => {
    setReferences([...references, ""]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", title);
    formData.append("sport_id", sportId);
    formData.append("content", content);
    references.forEach((ref, index) => {
      formData.append(`references[${index}]`, ref);
    });
    images.forEach((image) => {
      formData.append("images", image);
    });
    if (video) {
      formData.append("video", video);
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin-news/news/add`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      alert("News added successfully");
      setTitle("");
      setContent("");
      setSportId("");
      setReferences([""]);
      setImages([]);
      setVideo(null);
    } catch (error) {
      console.error("Error adding news:", error);
      alert("Failed to add news");
    }
  };

  return (
    <div
      style={{
        maxWidth: "600px",
        margin: "20px auto",
        padding: "20px",
        border: "1px solid #ccc",
        borderRadius: "5px",
      }}
      className="admin-news-write"
    >
      <style>{`
        @media (max-width: 1024px) {
          .admin-news-write { padding: 16px; margin: 16px auto; }
        }
        @media (max-width: 768px) {
          .admin-news-write { width: 95%; padding: 14px; }
          .admin-news-write .form-buttons { flex-direction: column; align-items: stretch; gap: 10px; }
          .admin-news-write .form-buttons button { width: 100%; }
        }
        @media (max-width: 480px) {
          .admin-news-write { width: 98%; padding: 12px; }
        }
      `}</style>
      {/* Header with buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "10px",
          borderBottom: "1px solid #eee",
        }}
        className="form-buttons"
      >
        <h2 style={{ margin: 0 }}>Write News Article</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="button"
            onClick={() => navigate("/DsportAdmin")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5a6268")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6c757d")}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              backgroundColor: "#e74c3c",
              color: "white",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c0392b")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#e74c3c")}
          >
            Logout
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
        />
        <select
          value={sportId}
          onChange={(e) => setSportId(e.target.value)}
          required
          style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
        >
          <option value="">Select Sport Category...</option>
          {Array.isArray(sports) && sports.map((sport) => (
            <option key={sport.id} value={sport.id}>
              {sport.name}
            </option>
          ))}
        </select>
        <textarea
          placeholder="Content (You can use Markdown formatting for better presentation)"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
          style={{
            width: "100%",
            height: "150px",
            padding: "8px",
            marginBottom: "10px",
            fontFamily: "monospace",
          }}
        />
        <small
          style={{ color: "#666", marginBottom: "10px", display: "block" }}
        >
          💡 Tip: Use Markdown syntax like **bold**, *italic*, # Headers, -
          Lists, etc.
        </small>

        <label>References:</label>
        {references.map((ref, index) => (
          <input
            key={index}
            type="url"
            placeholder="Reference URL"
            value={ref}
            onChange={(e) => handleReferenceChange(index, e.target.value)}
            style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
          />
        ))}
        <button
          type="button"
          onClick={addReferenceField}
          style={{ marginBottom: "10px", cursor: "pointer" }}
        >
          Add Reference
        </button>

        <label>Images:</label>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageChange}
          disabled={isCompressing}
          style={{ display: "block", marginBottom: "5px" }}
        />
        {isCompressing && (
          <div
            style={{ color: "#007BFF", marginBottom: "10px", fontSize: "14px" }}
          >
            🔄 Compressing images, please wait...
          </div>
        )}
        <small
          style={{ color: "#666", marginBottom: "15px", display: "block" }}
        >
          📷 Max 10MB per image. Images will be automatically compressed to
          optimize storage.
        </small>

        <label>Video:</label>
        <input
          type="file"
          accept="video/*"
          onChange={handleVideoChange}
          style={{ display: "block", marginBottom: "5px" }}
        />
        <small
          style={{ color: "#666", marginBottom: "15px", display: "block" }}
        >
          🎥 Max 70MB video file size allowed.
        </small>

        <button
          type="submit"
          style={{
            backgroundColor: "#007BFF",
            color: "white",
            padding: "10px",
            border: "none",
            cursor: "pointer",
            width: "100%",
          }}
        >
          Submit
        </button>
      </form>
    </div>
  );
};
